import db from 'services/firebase'
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  doc,
  serverTimestamp,
  writeBatch,
  query,
  where,
} from 'firebase/firestore'
import integrationTestsMapping from './integrationTestsMapping.json'

interface IntegrationTest {
  test_id: string
  name: string
  description: string
  framework: string
  category: string
  automated_type: boolean
  type: string
  status: string
  owner: string
}

interface IntegrationMapping {
  integrationId: string
  tests: IntegrationTest[]
}

interface IntegrationTestsMapping {
  [integrationName: string]: IntegrationMapping
}

interface TestEvaluationResult {
  test_id: string
  status: 'Pass' | 'Fail' | 'Not Applicable'
  score: number
  details: string
  last_checked: string
  conditions_met: string[]
  conditions_failed: string[]
}

interface IntegrationData {
  [key: string]: any
}

/**
 * Service to handle adding integration-specific tests to tenant collections
 */
export class IntegrationTestsService {
  private mapping: IntegrationTestsMapping = integrationTestsMapping

  /**
   * Adds integration-specific tests to a tenant's tests collection
   * @param integrationName - Name of the integration (e.g., 'GitHub', 'Jira')
   * @param tenantId - The tenant ID
   * @param currentUser - Current user object
   * @returns Promise with success status and added tests count
   */
  async addIntegrationTests(
    integrationName: string,
    tenantId: string,
    currentUser: any
  ): Promise<{ success: boolean; addedTestsCount: number; error?: string }> {
    try {
      if (!tenantId || !currentUser) {
        throw new Error('Tenant ID and current user are required')
      }

      const integrationData = this.mapping[integrationName]
      if (!integrationData || !integrationData.tests.length) {
        return { success: true, addedTestsCount: 0 }
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const testsRef = collection(tenantRef, 'tests')

      let addedTestsCount = 0
      const batch = writeBatch(db)

      const existingTestsSnapshot = await getDocs(testsRef)
      const existingTestIds = new Set(
        existingTestsSnapshot.docs.map((doc) => doc.id)
      )

      for (const test of integrationData.tests) {
        if (existingTestIds.has(test.test_id)) {
          continue
        }

        try {
          const tenantTestRef = doc(testsRef, test.test_id)
          batch.set(tenantTestRef, {
            test_id: test.test_id,
            name: test.name,
            description: test.description,
            framework: test.framework,
            category: test.category,
            automated_type: test.automated_type,
            type: test.type,
            status: test.status,
            owner: test.owner,
            integration: integrationName,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            tenantId,
            uid: currentUser.uid,
            integration_source: integrationName,
            last_evaluated: null,
            evaluation_score: 0,
            evaluation_details: '',
            conditions_met: [],
            conditions_failed: [],
          })
          
          addedTestsCount++
        } catch (error) {
          console.error(`Error processing test ${test.test_id}:`, error)
        }
      }

      if (addedTestsCount > 0) {
        await batch.commit()

        await this.logActivity(
          currentUser,
          `added ${addedTestsCount} integration-specific tests for ${integrationName}`,
          'Integration tests added',
          'Test Management'
        )
      }

      return { success: true, addedTestsCount }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, addedTestsCount: 0, error: errorMessage }
    }
  }

  /**
   * Removes integration-specific tests from a tenant's tests collection
   * @param integrationName - Name of the integration
   * @param tenantId - The tenant ID
   * @param currentUser - Current user object
   * @returns Promise with success status and removed tests count
   */
  async removeIntegrationTests(
    integrationName: string,
    tenantId: string,
    currentUser: any
  ): Promise<{ success: boolean; removedTestsCount: number; error?: string }> {
    try {
      if (!tenantId || !currentUser) {
        throw new Error('Tenant ID and current user are required')
      }

      const integrationData = this.mapping[integrationName]
      if (!integrationData || !integrationData.tests.length) {
        return { success: true, removedTestsCount: 0 }
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const testsRef = collection(tenantRef, 'tests')

      let removedTestsCount = 0
      const batch = writeBatch(db)

      for (const test of integrationData.tests) {
        try {
          const testDocRef = doc(testsRef, test.test_id)
          const testDoc = await getDoc(testDocRef)
          
          if (testDoc.exists()) {
            const testData = testDoc.data()
            
            if (testData.integration_source === integrationName) {
              batch.delete(testDocRef)
              removedTestsCount++
            }
          }
        } catch (error) {
          console.error(`Error removing test ${test.test_id}:`, error)
        }
      }

      if (removedTestsCount > 0) {
        await batch.commit()
        
        await this.logActivity(
          currentUser,
          `removed ${removedTestsCount} integration-specific tests for ${integrationName}`,
          'Integration tests removed',
          'Test Management'
        )
      }

      return { success: true, removedTestsCount }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, removedTestsCount: 0, error: errorMessage }
    }
  }

  /**
   * Gets the list of tests associated with an integration
   * @param integrationName - Name of the integration
   * @returns Array of test objects or empty array
   */
  getIntegrationTests(integrationName: string): IntegrationTest[] {
    const integrationData = this.mapping[integrationName]
    return integrationData?.tests || []
  }

  /**
   * Checks if an integration has associated tests
   * @param integrationName - Name of the integration
   * @returns Boolean indicating if integration has tests
   */
  hasIntegrationTests(integrationName: string): boolean {
    const integrationData = this.mapping[integrationName]
    return integrationData?.tests?.length > 0
  }

  /**
   * Gets all available integrations with their test mappings
   * @returns Object containing all integration mappings
   */
  getAllIntegrationMappings(): IntegrationTestsMapping {
    return this.mapping
  }

  /**
   * Evaluates a single test based on integration data
   * @param testId - The test ID to evaluate
   * @param integrationName - Name of the integration
   * @param integrationData - Data from the integration
   * @param tenantId - The tenant ID for database queries
   * @returns TestEvaluationResult
   */
  async evaluateTest(
    testId: string,
    integrationName: string,
    integrationData: IntegrationData,
    tenantId: string
  ): Promise<TestEvaluationResult> {
    const test = this.getTestById(testId, integrationName)
    if (!test) {
      return {
        test_id: testId,
        status: 'Not Applicable',
        score: 0,
        details: 'Test not found',
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Test definition not found'],
      }
    }

    switch (testId) {
      case 'bXsKo4jky6oNjOkpDbVp': // Company has a version control system
        return await this.evaluateVersionControlSystem(integrationData, tenantId)
      
      case '2qUShD7Hu7Ixr5z93OmL': // GitHub accounts associated with users
        return await this.evaluateGitHubAccountsAssociated(integrationData)
      
      case 'eGQNCE2Dsx92h9ilQOvc': // GitHub accounts deprovisioned when personnel leave
        return await this.evaluateGitHubAccountsDeprovisioned(integrationData)
      
      case 'X7x3FvfaAnnebDmCTdZy': // GitHub repository visibility has been set to private
        return await this.evaluateGitHubRepositoryVisibility(integrationData, tenantId)
      
      case 'DUvsEOZca5pADV6j2Rfc': // Inventory items have active owners
        return await this.evaluateInventoryItemsOwners(integrationData, tenantId)
      
      case 'JBxJx90qGIqK6XZNcSUL': // Inventory items have descriptions
        return await this.evaluateInventoryItemsDescriptions(integrationData, tenantId)
      
      case 'fLHSnkTew2CKZeAxaoLd': // MFA on GitHub
        return await this.evaluateGitHubMFA(integrationData, tenantId)
      
      case 'StONjKHNwZ9OSCRVgKx8': // Critical vulnerabilities
        return await this.evaluateDependabotVulnerabilitiesBySeverity('critical', 'StONjKHNwZ9OSCRVgKx8', tenantId)
      
      case 'IOIScuzSIgchOlCSnhid': // High vulnerabilities
        return await this.evaluateDependabotVulnerabilitiesBySeverity('high', 'IOIScuzSIgchOlCSnhid', tenantId)
      
      case '9mHxdFKKtoGqYwyF76dC': // Medium vulnerabilities
        return await this.evaluateDependabotVulnerabilitiesBySeverity('medium', '9mHxdFKKtoGqYwyF76dC', tenantId)
      
      case 'Ksdi20LbVnIb8QnfoIrE': // Low vulnerabilities
        return await this.evaluateDependabotVulnerabilitiesBySeverity('low', 'Ksdi20LbVnIb8QnfoIrE', tenantId)
      
      default:
        return {
          test_id: testId,
          status: 'Not Applicable',
          score: 0,
          details: 'No evaluator available for this test',
          last_checked: new Date().toISOString(),
          conditions_met: [],
          conditions_failed: ['No evaluator implemented'],
        }
    }
  }

  /**
   * Evaluates all tests for an integration
   * @param integrationName - Name of the integration
   * @param tenantId - The tenant ID
   * @param integrationData - Data from the integration
   * @returns Promise with evaluation results
   */
  async evaluateAllIntegrationTests(
    integrationName: string,
    tenantId: string,
    integrationData: IntegrationData
  ): Promise<{ success: boolean; results: TestEvaluationResult[]; error?: string }> {
    try {
      const integrationData = this.mapping[integrationName]
      if (!integrationData || !integrationData.tests.length) {
        return { success: true, results: [] }
      }

      const results: TestEvaluationResult[] = []
      const batch = writeBatch(db)
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsRef = collection(tenantRef, 'tests')

      for (const test of integrationData.tests) {
        const evaluation = await this.evaluateTest(test.test_id, integrationName, integrationData, tenantId)
        results.push(evaluation)

        const testDocRef = doc(testsRef, test.test_id)
        batch.update(testDocRef, {
          status: evaluation.status,
          last_evaluated: serverTimestamp(),
          evaluation_score: evaluation.score,
          evaluation_details: evaluation.details,
          conditions_met: evaluation.conditions_met,
          conditions_failed: evaluation.conditions_failed,
          updated_at: serverTimestamp(),
        })
      }

      await batch.commit()
      return { success: true, results }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, results: [], error: errorMessage }
    }
  }

  /**
   * Gets a test by ID from the mapping
   */
  private getTestById(testId: string, integrationName: string): IntegrationTest | null {
    const integrationData = this.mapping[integrationName]
    if (!integrationData) return null
    
    return integrationData.tests.find(test => test.test_id === testId) || null
  }

  private async evaluateVersionControlSystem(integrationData: IntegrationData, tenantId: string): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'), where('is_repo', '==', true))
      const assetsSnapshot = await getDocs(githubAssetsQuery)
      
      const repos = assetsSnapshot.docs.map(doc => doc.data())

      if (repos.length > 0) {
        conditions_met.push('Repositories exist in assets')
        score = 100
        
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        const recentRepos = repos.filter((repo: any) => 
          repo.updated_at && new Date(repo.updated_at) > thirtyDaysAgo
        )

        if (recentRepos.length > 0) {
          conditions_met.push('Repositories updated in last 30 days')
        } else {
          conditions_met.push('Repositories exist but not recently updated')
        }

        const reposWithCommits = repos.filter((repo: any) => 
          repo.pushed_at && new Date(repo.pushed_at) > thirtyDaysAgo
        )

        if (reposWithCommits.length > 0) {
          conditions_met.push('Repositories have recent commits')
        } else {
          conditions_met.push('Repositories exist but no recent commits')
        }
      } else {
        conditions_failed.push('No repositories found in assets')
        score = 0
      }

      const status = score >= 70 ? 'Pass' : 'Fail'
      const details = `Found ${repos.length} repositories in assets`

      return {
        test_id: 'bXsKo4jky6oNjOkpDbVp',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    } catch (error) {
      return {
        test_id: 'bXsKo4jky6oNjOkpDbVp',
        status: 'Fail',
        score: 0,
        details: `Error checking repositories: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Error accessing assets collection'],
      }
    }
  }

  private async evaluateGitHubAccountsAssociated(integrationData: IntegrationData): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const accessReviewQuery = query(
        collection(db, 'tenants', integrationData.tenantId, 'access_review'),
        where('platform', '==', 'GitHub')
      )
      
      const accessReviewSnapshot = await getDocs(accessReviewQuery)
      
      if (accessReviewSnapshot.empty) {
        conditions_failed.push('No access review data found for GitHub')
        return {
          test_id: '2qUShD7Hu7Ixr5z93OmL',
          status: 'Fail',
          score: 0,
          details: 'No access review data found for GitHub integration',
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      const accessReviewData = accessReviewSnapshot.docs[0].data()
      const members = accessReviewData.members || {}
      
      const memberKeys = Object.keys(members)
      if (memberKeys.length === 0) {
        conditions_failed.push('No members found in access review')
        return {
          test_id: '2qUShD7Hu7Ixr5z93OmL',
          status: 'Fail',
          score: 0,
          details: 'No members found in GitHub access review',
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      let membersWithOwners = 0
      const totalMembers = memberKeys.length

      memberKeys.forEach(memberKey => {
        const member = members[memberKey]
        if (member.owner && member.owner.id && member.owner.name) {
          membersWithOwners++
          conditions_met.push(`Member ${member.login || memberKey} has owner assigned`)
        } else {
          conditions_failed.push(`Member ${member.login || memberKey} missing owner assignment`)
        }
      })

      const ownerPercentage = (membersWithOwners / totalMembers) * 100
      score = Math.round(ownerPercentage)
      
      const status = membersWithOwners === totalMembers ? 'Pass' : 'Fail'
      
      const details = `Found ${totalMembers} members, ${membersWithOwners} have owners assigned (${ownerPercentage.toFixed(1)}%)`

      return {
        test_id: '2qUShD7Hu7Ixr5z93OmL',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }

    } catch (error) {
      console.error('Error evaluating GitHub accounts associated test:', error)
      conditions_failed.push(`Evaluation error: ${error instanceof Error ? error.message : 'Unknown error'}`)
      
      return {
        test_id: '2qUShD7Hu7Ixr5z93OmL',
        status: 'Fail',
        score: 0,
        details: `Evaluation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    }
  }

  private async evaluateGitHubAccountsDeprovisioned(integrationData: IntegrationData): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const accessReviewQuery = query(
        collection(db, 'tenants', integrationData.tenantId, 'access_review'),
        where('platform', '==', 'GitHub')
      )
      
      const accessReviewSnapshot = await getDocs(accessReviewQuery)
      
      if (accessReviewSnapshot.empty) {
        conditions_failed.push('No access review data found for GitHub')
        return {
          test_id: 'eGQNCE2Dsx92h9ilQOvc',
          status: 'Fail',
          score: 0,
          details: 'No access review data found for GitHub integration',
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      const accessReviewData = accessReviewSnapshot.docs[0].data()
      const members = accessReviewData.members || {}
      
      const memberKeys = Object.keys(members)
      if (memberKeys.length === 0) {
        conditions_failed.push('No members found in access review')
        return {
          test_id: 'eGQNCE2Dsx92h9ilQOvc',
          status: 'Fail',
          score: 0,
          details: 'No members found in GitHub access review',
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      let activeMembers = 0
      let inactiveMembers = 0
      const totalMembers = memberKeys.length

      memberKeys.forEach(memberKey => {
        const member = members[memberKey]
        if (member.status === 'active') {
          activeMembers++
          conditions_met.push(`Member ${member.login || memberKey} is active`)
        } else if (member.status === 'inactive') {
          inactiveMembers++
          conditions_failed.push(`Member ${member.login || memberKey} is inactive (should be deprovisioned)`)
        } else {
          conditions_failed.push(`Member ${member.login || memberKey} has no status or unknown status`)
        }
      })

      const hasInactiveMembers = inactiveMembers > 0
      const status = hasInactiveMembers ? 'Fail' : 'Pass'

      const activePercentage = (activeMembers / totalMembers) * 100
      score = hasInactiveMembers ? 0 : Math.round(activePercentage)
      
      const details = `Found ${totalMembers} members: ${activeMembers} active, ${inactiveMembers} inactive. ${hasInactiveMembers ? 'Test fails due to inactive members that should be deprovisioned.' : 'All members are properly active.'}`

      return {
        test_id: 'eGQNCE2Dsx92h9ilQOvc',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }

    } catch (error) {
      console.error('Error evaluating GitHub accounts deprovisioned test:', error)
      conditions_failed.push(`Evaluation error: ${error instanceof Error ? error.message : 'Unknown error'}`)
      
      return {
        test_id: 'eGQNCE2Dsx92h9ilQOvc',
        status: 'Fail',
        score: 0,
        details: `Evaluation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    }
  }

  private async evaluateGitHubRepositoryVisibility(integrationData: IntegrationData, tenantId: string): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'), where('is_repo', '==', true))
      const assetsSnapshot = await getDocs(githubAssetsQuery)
      
      const repos = assetsSnapshot.docs.map(doc => doc.data())

      if (repos.length === 0) {
        conditions_failed.push('No repositories found in assets')
        return {
          test_id: 'X7x3FvfaAnnebDmCTdZy',
          status: 'Fail',
          score: 0,
          details: 'No GitHub repositories found in assets',
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      conditions_met.push(`Found ${repos.length} repositories in assets`)

      const forkedRepos = repos.filter((repo: any) => repo.fork === true || repo.is_fork === true)
      const nonForkedRepos = repos.filter((repo: any) => !repo.fork && !repo.is_fork)

      if (forkedRepos.length > 0) {
        conditions_met.push(`Found ${forkedRepos.length} forked repositories (excluded from test)`)
      }

      if (nonForkedRepos.length === 0) {
        conditions_met.push('All repositories are forked (test passes by default)')
        return {
          test_id: 'X7x3FvfaAnnebDmCTdZy',
          status: 'Pass',
          score: 100,
          details: 'All repositories are forked from external sources, test passes by default',
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      const privateNonForkedRepos = nonForkedRepos.filter((repo: any) => repo.visibility === 'Private')
      const publicNonForkedRepos = nonForkedRepos.filter((repo: any) => repo.visibility === 'Public')

      const hasPublicNonForkedRepos = publicNonForkedRepos.length > 0
      const status = hasPublicNonForkedRepos ? 'Fail' : 'Pass'

      if (hasPublicNonForkedRepos) {
        conditions_failed.push(`${publicNonForkedRepos.length} non-forked repositories are public`)
        publicNonForkedRepos.forEach((repo: any) => {
          conditions_failed.push(`Repository "${repo.repo_name}" is public (should be private)`)
        })
        score = 0
      } else {
        conditions_met.push('All non-forked repositories are private')
        score = 100
      }

      const details = `Found ${repos.length} total repositories: ${nonForkedRepos.length} non-forked (${privateNonForkedRepos.length} private, ${publicNonForkedRepos.length} public), ${forkedRepos.length} forked. ${hasPublicNonForkedRepos ? 'Test fails: non-forked repositories should be private.' : 'Test passes: all non-forked repositories are private.'}`

      return {
        test_id: 'X7x3FvfaAnnebDmCTdZy',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    } catch (error) {
      return {
        test_id: 'X7x3FvfaAnnebDmCTdZy',
        status: 'Fail',
        score: 0,
        details: `Error checking repository visibility: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Error accessing assets collection'],
      }
    }
  }

  private async evaluateInventoryItemsOwners(integrationData: IntegrationData, tenantId: string): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'), where('is_repo', '==', true))
      const assetsSnapshot = await getDocs(githubAssetsQuery)
      
      const repos = assetsSnapshot.docs.map(doc => doc.data())

      if (repos.length > 0) {
        conditions_met.push('Inventory items (repositories) found in assets')
        score += 30
      } else {
        conditions_failed.push('No inventory items found in assets')
      }

      const reposWithOwners = repos.filter((repo: any) => repo.owner && repo.owner.login)
      if (reposWithOwners.length > 0) {
        conditions_met.push('Repositories have owners')
        score += 40
      } else {
        conditions_failed.push('No repository owners found')
      }

      if (repos.length > 0 && reposWithOwners.length === repos.length) {
        conditions_met.push('All repositories have owners')
        score += 30
      } else if (repos.length > 0) {
        conditions_failed.push('Some repositories missing owners')
      }

      const status = score >= 70 ? 'Pass' : 'Fail'
      const details = `Found ${repos.length} repositories in assets, ${reposWithOwners.length} with owners`

      return {
        test_id: 'DUvsEOZca5pADV6j2Rfc',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    } catch (error) {
      return {
        test_id: 'DUvsEOZca5pADV6j2Rfc',
        status: 'Fail',
        score: 0,
        details: `Error checking repository owners: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Error accessing assets collection'],
      }
    }
  }

  private async evaluateInventoryItemsDescriptions(integrationData: IntegrationData, tenantId: string): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'), where('is_repo', '==', true))
      const assetsSnapshot = await getDocs(githubAssetsQuery)
      
      const repos = assetsSnapshot.docs.map(doc => doc.data())

      if (repos.length > 0) {
        conditions_met.push('Inventory items (repositories) found in assets')
        score += 30
      } else {
        conditions_failed.push('No inventory items found in assets')
      }

      const reposWithDescriptions = repos.filter((repo: any) => repo.description && repo.description.trim())
      if (reposWithDescriptions.length > 0) {
        conditions_met.push('Repositories have descriptions')
        score += 40
      } else {
        conditions_failed.push('No repository descriptions found')
      }

      if (repos.length > 0 && reposWithDescriptions.length === repos.length) {
        conditions_met.push('All repositories have descriptions')
        score += 30
      } else if (repos.length > 0) {
        conditions_failed.push('Some repositories missing descriptions')
      }

      const status = score >= 70 ? 'Pass' : 'Fail'
      const details = `Found ${repos.length} repositories in assets, ${reposWithDescriptions.length} with descriptions`

      return {
        test_id: 'JBxJx90qGIqK6XZNcSUL',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    } catch (error) {
      return {
        test_id: 'JBxJx90qGIqK6XZNcSUL',
        status: 'Fail',
        score: 0,
        details: `Error checking repository descriptions: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Error accessing assets collection'],
      }
    }
  }

  private async evaluateGitHubMFA(integrationData: IntegrationData, tenantId: string): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const accessReviewRef = doc(tenantRef, 'access_review', 'github')
      const accessReviewDoc = await getDoc(accessReviewRef)
      
      let members: any[] = []
      if (accessReviewDoc.exists()) {
        const data = accessReviewDoc.data()
        members = Object.values(data.members || {})
      }

      if (members.length > 0) {
        conditions_met.push('GitHub accounts found in access review')
        score += 20
      } else {
        conditions_failed.push('No GitHub accounts found in access review')
      }

      const accountsWithMFA = members.filter((member: any) => member.isTwoFactorEnabled === true)
      if (accountsWithMFA.length > 0) {
        conditions_met.push('MFA enabled for some accounts')
        score += 40
      } else {
        conditions_failed.push('No MFA enabled accounts found')
      }

      if (members.length > 0 && accountsWithMFA.length === members.length) {
        conditions_met.push('MFA enabled for all accounts')
        score += 40
      } else if (members.length > 0) {
        conditions_failed.push('Some accounts missing MFA')
      }

      const status = score >= 70 ? 'Pass' : 'Fail'
      const details = `Found ${members.length} GitHub accounts in access review, ${accountsWithMFA.length} with MFA enabled`

      return {
        test_id: 'fLHSnkTew2CKZeAxaoLd',
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    } catch (error) {
      return {
        test_id: 'fLHSnkTew2CKZeAxaoLd',
        status: 'Fail',
        score: 0,
        details: `Error checking GitHub MFA: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Error accessing access review collection'],
      }
    }
  }

  private async evaluateDependabotVulnerabilitiesBySeverity(
    severity: 'critical' | 'high' | 'medium' | 'low',
    testId: string,
    tenantId: string
  ): Promise<TestEvaluationResult> {
    const conditions_met: string[] = []
    const conditions_failed: string[] = []
    let score = 0

    try {
      const tenantRef = doc(db, 'tenants', tenantId)
      const vulnerabilitiesRef = collection(tenantRef, 'vulnerabilities')
      const vulnsQuery = query(
        vulnerabilitiesRef, 
        where('source', '==', 'Github Dependabot'),
        where('severity', '==', severity)
      )
      
      const vulnsSnapshot = await getDocs(vulnsQuery)
      const vulnerabilities = vulnsSnapshot.docs.map(doc => doc.data())
      
      if (vulnerabilities.length === 0) {
        conditions_met.push(`No ${severity} vulnerabilities found from GitHub Dependabot`)
        score = 100
        return {
          test_id: testId,
          status: 'Pass',
          score,
          details: `No ${severity} vulnerabilities found in GitHub Dependabot scan`,
          last_checked: new Date().toISOString(),
          conditions_met,
          conditions_failed,
        }
      }

      const resolvedVulns = vulnerabilities.filter((vuln: any) => 
        vuln.remediation_status === 'resolved' || vuln.status === 'closed'
      )
      const pendingVulns = vulnerabilities.filter((vuln: any) => 
        vuln.remediation_status === 'pending' || vuln.status === 'open'
      )

      if (resolvedVulns.length > 0) {
        conditions_met.push(`${resolvedVulns.length} ${severity} vulnerabilities have been resolved`)
        score += 50
      }

      if (pendingVulns.length > 0) {
        conditions_failed.push(`${pendingVulns.length} ${severity} vulnerabilities are still pending resolution`)
        pendingVulns.forEach((vuln: any) => {
          conditions_failed.push(`${severity.charAt(0).toUpperCase() + severity.slice(1)} vulnerability in ${vuln.package || 'unknown package'} (${vuln.cve_id || 'no CVE'}) is pending`)
        })
        score = Math.max(0, score - 50)
      }

      const status = pendingVulns.length === 0 ? 'Pass' : 'Fail'
      const details = `Found ${vulnerabilities.length} ${severity} vulnerabilities: ${resolvedVulns.length} resolved, ${pendingVulns.length} pending`

      return {
        test_id: testId,
        status,
        score,
        details,
        last_checked: new Date().toISOString(),
        conditions_met,
        conditions_failed,
      }
    } catch (error) {
      return {
        test_id: testId,
        status: 'Fail',
        score: 0,
        details: `Error checking ${severity} vulnerabilities: ${error instanceof Error ? error.message : 'Unknown error'}`,
        last_checked: new Date().toISOString(),
        conditions_met: [],
        conditions_failed: ['Error accessing vulnerabilities collection'],
      }
    }
  }


  /**
   * Logs activity to the tenant's activities collection
   * @param currentUser - Current user object
   * @param message - Activity message
   * @param action - Action type
   * @param target - Target system
   */
  private async logActivity(
    currentUser: any,
    message: string,
    action: string,
    target: string
  ): Promise<void> {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const activitiesRef = collection(tenantRef, 'activities')

      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`

      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} ${message}`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: action,
        target: target,
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
    } catch (error) {
      console.error('Error logging activity:', error)
    }
  }
}

export const integrationTestsService = new IntegrationTestsService()

export const addIntegrationTests = (
  integrationName: string,
  tenantId: string,
  currentUser: any
) => integrationTestsService.addIntegrationTests(integrationName, tenantId, currentUser)

export const removeIntegrationTests = (
  integrationName: string,
  tenantId: string,
  currentUser: any
) => integrationTestsService.removeIntegrationTests(integrationName, tenantId, currentUser)

export const getIntegrationTests = (integrationName: string) =>
  integrationTestsService.getIntegrationTests(integrationName)

export const hasIntegrationTests = (integrationName: string) =>
  integrationTestsService.hasIntegrationTests(integrationName)

export const evaluateAllIntegrationTests = (
  integrationName: string,
  tenantId: string,
  integrationData: IntegrationData
) => integrationTestsService.evaluateAllIntegrationTests(integrationName, tenantId, integrationData)

export const evaluateTest = (
  testId: string,
  integrationName: string,
  integrationData: IntegrationData,
  tenantId: string
) => integrationTestsService.evaluateTest(testId, integrationName, integrationData, tenantId)
